import { useId } from 'react';
import { palette } from '../theme';
import { useIsPhone } from '../hooks/useIsPhone';
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '../primitives';

export type PreferenceChannelKey = 'email' | 'sms' | 'push' | 'in-app';

export interface PreferenceCategoryRow {
  id: string;
  label: string;
  description?: string;
  channels: Record<PreferenceChannelKey, boolean>;
  /** Rendered read-only — a category the user is not allowed to switch off. */
  locked?: boolean;
  /** Shown beside a locked category to explain why. */
  lockedHint?: string;
}

export interface NotificationPreferencesPanelProps {
  categories: PreferenceCategoryRow[];
  channels: { key: PreferenceChannelKey; label: string }[];
  digest: 'immediate' | 'daily';
  quietHours: { start: string; end: string } | null;
  timezone: string;
  timezones: string[];
  loading?: boolean;
  saving?: boolean;
  onToggle: (categoryId: string, channel: PreferenceChannelKey, next: boolean) => void;
  onDigestChange: (next: 'immediate' | 'daily') => void;
  onQuietHoursChange: (next: { start: string; end: string } | null) => void;
  onTimezoneChange: (next: string) => void;
  labels: {
    categoriesHeading: string;
    deliveryHeading: string;
    digestLabel: string;
    digestImmediate: string;
    digestDaily: string;
    digestHint: string;
    quietHoursLabel: string;
    quietHoursHint: string;
    quietHoursFrom: string;
    quietHoursTo: string;
    timezoneLabel: string;
    loading: string;
  };
}

const DEFAULT_QUIET_HOURS = { start: '22:00', end: '07:00' };

/**
 * The Notifications settings panel, shared by the Officer Portal, the Admin
 * Portal and the participant portal.
 *
 * Presentational: state and persistence belong to the screen that renders it,
 * and every string is passed in, because `libs/ui` has no i18n context of its
 * own. Three portals, one grid — a channel added to the product appears in all
 * of them without three near-identical layouts drifting apart.
 *
 * Mandatory categories render as a disabled, still-on row with a hint rather
 * than being hidden. A user who cannot find "account" in the list would
 * reasonably conclude their password-reset mail was governed by some switch
 * they had already flipped; showing it locked answers the question instead.
 */
export function NotificationPreferencesPanel({
  categories,
  channels,
  digest,
  quietHours,
  timezone,
  timezones,
  loading = false,
  saving = false,
  onToggle,
  onDigestChange,
  onQuietHoursChange,
  onTimezoneChange,
  labels,
}: NotificationPreferencesPanelProps) {
  const isPhone = useIsPhone();

  if (loading) {
    return (
      <Stack sx={{ alignItems: 'center', py: 4 }}>
        <CircularProgress size={22} aria-label={labels.loading} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ opacity: saving ? 0.6 : 1, transition: 'opacity 200ms' }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {labels.categoriesHeading}
        </Typography>

        {/* A table, not a grid of cards: the question a user actually has is
            "which of these columns is on for that row", and a table answers it
            by scanning. On a phone the columns do not fit beside the category
            text (four French channel headers alone are wider than a 390px
            card), so each category becomes a block with its own labelled
            switches underneath; the channel name travels with the switch
            because there is no header row left to read it from. From `sm` up
            the table fits even with the French labels, and keeps a horizontal
            scroll only as a last resort for an unusually narrow container. */}
        {isPhone ? (
          <Box>
            {categories.map((category) => (
              <StackedCategory
                key={category.id}
                category={category}
                channels={channels}
                saving={saving}
                onToggle={onToggle}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{ width: '100%', minWidth: 440, borderCollapse: 'collapse' }}
            >
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" scope="col" sx={headerCellSx} />
                  {channels.map((channel) => (
                    <Box
                      key={channel.key}
                      component="th"
                      scope="col"
                      sx={{ ...headerCellSx, textAlign: 'center' }}
                    >
                      {channel.label}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {categories.map((category) => (
                  <Box component="tr" key={category.id}>
                    <Box component="th" scope="row" sx={{ ...bodyCellSx, textAlign: 'left' }}>
                      <CategoryText category={category} />
                    </Box>
                    {channels.map((channel) => (
                      <Box
                        key={channel.key}
                        component="td"
                        sx={{ ...bodyCellSx, textAlign: 'center' }}
                      >
                        <Switch
                          size="small"
                          checked={category.locked ? true : category.channels[channel.key]}
                          disabled={category.locked || saving}
                          slotProps={{
                            input: { 'aria-label': switchName(category, channel) },
                          }}
                          onChange={(_event, next) => onToggle(category.id, channel.key, next)}
                        />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          {labels.deliveryHeading}
        </Typography>

        <Stack spacing={2}>
          <FormControl size="small" sx={{ maxWidth: 320 }}>
            <InputLabel id="notification-digest-label">{labels.digestLabel}</InputLabel>
            <Select
              labelId="notification-digest-label"
              label={labels.digestLabel}
              value={digest}
              disabled={saving}
              onChange={(event) => onDigestChange(event.target.value as 'immediate' | 'daily')}
            >
              <MenuItem value="immediate">{labels.digestImmediate}</MenuItem>
              <MenuItem value="daily">{labels.digestDaily}</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: palette.text.muted }}>
            {labels.digestHint}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={quietHours !== null}
                disabled={saving}
                onChange={(_event, next) => onQuietHoursChange(next ? DEFAULT_QUIET_HOURS : null)}
              />
            }
            label={labels.quietHoursLabel}
          />
          <Typography variant="caption" sx={{ color: palette.text.muted, mt: -1 }}>
            {labels.quietHoursHint}
          </Typography>

          {quietHours && (
            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
              <TextField
                size="small"
                type="time"
                label={labels.quietHoursFrom}
                value={quietHours.start}
                disabled={saving}
                onChange={(event) =>
                  onQuietHoursChange({ ...quietHours, start: event.target.value })
                }
                sx={{ width: 150 }}
              />
              <TextField
                size="small"
                type="time"
                label={labels.quietHoursTo}
                value={quietHours.end}
                disabled={saving}
                onChange={(event) => onQuietHoursChange({ ...quietHours, end: event.target.value })}
                sx={{ width: 150 }}
              />
            </Stack>
          )}

          <FormControl size="small" sx={{ maxWidth: 320 }}>
            <InputLabel id="notification-timezone-label">{labels.timezoneLabel}</InputLabel>
            <Select
              labelId="notification-timezone-label"
              label={labels.timezoneLabel}
              value={timezone}
              disabled={saving}
              onChange={(event) => onTimezoneChange(String(event.target.value))}
            >
              {timezones.map((zone) => (
                <MenuItem key={zone} value={zone}>
                  {zone}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>
    </Stack>
  );
}

type ChannelOption = NotificationPreferencesPanelProps['channels'][number];

/**
 * The switch's accessible name carries both halves of the question — the
 * category and the channel — in either layout, so a screen-reader user moving
 * switch to switch never hears a bare "Email" without knowing for what.
 */
function switchName(category: PreferenceCategoryRow, channel: ChannelOption) {
  return `${category.label} — ${channel.label}`;
}

function CategoryText({ category, id }: { category: PreferenceCategoryRow; id?: string }) {
  const hint = category.locked ? category.lockedHint : category.description;
  return (
    <>
      <Typography id={id} variant="body2" sx={{ fontWeight: 600 }}>
        {category.label}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ display: 'block', color: palette.text.muted }}>
          {hint}
        </Typography>
      )}
    </>
  );
}

/** One category on a phone: its text, then a wrapping row of labelled switches. */
function StackedCategory({
  category,
  channels,
  saving,
  onToggle,
}: {
  category: PreferenceCategoryRow;
  channels: ChannelOption[];
  saving: boolean;
  onToggle: NotificationPreferencesPanelProps['onToggle'];
}) {
  const titleId = useId();
  return (
    <Box
      role="group"
      aria-labelledby={titleId}
      sx={{ py: 1.25, borderBottom: `1px solid ${palette.border.line}` }}
    >
      <CategoryText category={category} id={titleId} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mt: 0.75 }}>
        {channels.map((channel) => (
          <FormControlLabel
            key={channel.key}
            sx={{ ml: 0, mr: 0 }}
            control={
              <Switch
                size="small"
                checked={category.locked ? true : category.channels[channel.key]}
                disabled={category.locked || saving}
                slotProps={{ input: { 'aria-label': switchName(category, channel) } }}
                onChange={(_event, next) => onToggle(category.id, channel.key, next)}
              />
            }
            label={
              <Typography variant="body2" component="span" sx={{ fontSize: 13 }}>
                {channel.label}
              </Typography>
            }
          />
        ))}
      </Box>
    </Box>
  );
}

/**
 * Translate function shape. `libs/ui` deliberately does not depend on
 * react-i18next — the apps own their i18n instance — so the mapping helpers
 * below take one of these instead of reaching for a hook.
 */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** The shared `common.notifications.*` key space, in the order they render. */
export const PREFERENCE_CHANNEL_KEYS: PreferenceChannelKey[] = ['email', 'sms', 'push', 'in-app'];

/**
 * Turns a `NotificationPreferencesDto` into panel rows.
 *
 * Lives here rather than in each portal because all three portals render the
 * identical taxonomy from the identical key space; three copies of this mapping
 * is three chances to forget a category when one is added.
 */
export function buildPreferenceRows(
  preferences: {
    categories: Record<string, Record<string, boolean>>;
    mandatoryCategories: readonly string[];
  },
  t: TranslateFn,
): PreferenceCategoryRow[] {
  return Object.entries(preferences.categories).map(([id, channels]) => ({
    id,
    label: t(`common.notifications.categories.${id}`),
    description: t(`common.notifications.categoryHints.${id}`),
    locked: preferences.mandatoryCategories.includes(id),
    lockedHint: t('common.notifications.settings.mandatoryHint'),
    channels: {
      email: Boolean(channels['email']),
      sms: Boolean(channels['sms']),
      push: Boolean(channels['push']),
      'in-app': Boolean(channels['in-app']),
    },
  }));
}

export function buildChannelOptions(t: TranslateFn): NotificationPreferencesPanelProps['channels'] {
  return PREFERENCE_CHANNEL_KEYS.map((key) => ({
    key,
    label: t(`common.notifications.channels.${key}`),
  }));
}

export function buildPanelLabels(t: TranslateFn): NotificationPreferencesPanelProps['labels'] {
  return {
    categoriesHeading: t('common.notifications.settings.categoriesHeading'),
    deliveryHeading: t('common.notifications.settings.deliveryHeading'),
    digestLabel: t('common.notifications.settings.digestLabel'),
    digestImmediate: t('common.notifications.settings.digestImmediate'),
    digestDaily: t('common.notifications.settings.digestDaily'),
    digestHint: t('common.notifications.settings.digestHint'),
    quietHoursLabel: t('common.notifications.settings.quietHoursLabel'),
    quietHoursHint: t('common.notifications.settings.quietHoursHint'),
    quietHoursFrom: t('common.notifications.settings.quietHoursFrom'),
    quietHoursTo: t('common.notifications.settings.quietHoursTo'),
    timezoneLabel: t('common.notifications.settings.timezoneLabel'),
    loading: t('common.notifications.loading'),
  };
}

export function buildBellLabels(t: TranslateFn, unread: number) {
  return {
    title: t('common.notifications.title'),
    titleUnread: t('common.notifications.titleUnread', { count: unread }),
    heading: t('common.notifications.heading'),
    empty: t('common.notifications.empty'),
    markAllRead: t('common.notifications.markAllRead'),
    loading: t('common.notifications.loading'),
  };
}

/**
 * Timezones offered in the panel. A short, curated list of the Canadian zones a
 * municipality actually sits in — the full IANA database is 400 entries and a
 * user picking their own timezone is choosing between six, not four hundred.
 */
export const SUPPORTED_TIMEZONES = [
  'America/St_Johns',
  'America/Halifax',
  'America/Moncton',
  'America/Toronto',
  'America/Winnipeg',
  'America/Edmonton',
  'America/Vancouver',
];

const headerCellSx = {
  py: 1,
  px: 1,
  fontFamily: "'Manrope', sans-serif",
  fontSize: 12,
  fontWeight: 700,
  color: palette.text.secondary,
  borderBottom: `1px solid ${palette.border.line}`,
} as const;

const bodyCellSx = {
  py: 1,
  px: 1,
  fontWeight: 400,
  borderBottom: `1px solid ${palette.border.line}`,
} as const;
